import React, { ReactNode } from "react";
import Position from "../../types/Position";
import {
    CodeStatus,
    MaritimeEventType,
    PermitCategory,
    PermitType,
    WorkPermitTagType,
    getHighestPermitCategory
} from "../../common/enums";
import { getPermitIcon } from "../../icons";
import { Circle, Marker, Polygon, Polyline, Popup } from "react-leaflet";
import { permitPageUrl, versionedSequenceIdentifier } from "../../common/permitUtils";
import Resources from "../../types/Resources";
import { Date_toDateString } from "../../common/dateUtils";
import WorkPermitTag from "../../types/WorkPermitTag";
import User from "../../types/User";
import L from "leaflet";

type PositionMarkerProps = {
    resources: Resources;
    position: Position;
    permitInfo: {
        status: CodeStatus;
        permitCategories: PermitCategory[];
        permitName: PermitType;
        departmentId: number;
        sequenceIdentifier: string;
        version: number;
        isAnonymousPermit: boolean;
        documentReferenceId?: string;
        startDate?: Date;
        endDate?: Date;
        entrepreneur?: string;
        companyName?: string;
        workPermitTagsMap?: Map<WorkPermitTagType, WorkPermitTag>;
        user?: User;
        description?: string;
        maritimeEventType?: MaritimeEventType;
    };
};

const ICON_WIDTH = 30;
const ICON_HEIGHT = 42;
const ICON_ANCHOR_X = ICON_WIDTH / 2;
const ICON_ANCHOR_Y = ICON_HEIGHT;
const POPUP_ANCHOR_X = 0;
const POPUP_ANCHOR_Y = -35;

function PositionMarker({ position, permitInfo, resources }: PositionMarkerProps) {
    const iconCategory = position.category ?? getHighestPermitCategory(permitInfo.permitCategories);

    const { markerSrc } = getPermitIcon(
        permitInfo.permitName,
        iconCategory,
        permitInfo.status,
        position.isActive,
        permitInfo.maritimeEventType
    );

    const markerIcon = L.icon({
        iconUrl: markerSrc,
        iconSize: [ICON_WIDTH, ICON_HEIGHT],
        iconAnchor: [ICON_ANCHOR_X, ICON_ANCHOR_Y],
        popupAnchor: [POPUP_ANCHOR_X, POPUP_ANCHOR_Y]
    });

    const permitUrl = permitInfo.documentReferenceId
        ? permitPageUrl({
              permitType: permitInfo.permitName,
              departmentId: permitInfo.departmentId,
              documentReferenceId: permitInfo.documentReferenceId
          })
        : "";

    const contactPerson = !!permitInfo.workPermitTagsMap
        ? permitInfo.workPermitTagsMap.get(WorkPermitTagType.ContactPerson)
        : null;

    const workLeaderPhoneNumber = !!permitInfo.workPermitTagsMap
        ? permitInfo.workPermitTagsMap.get(WorkPermitTagType.WorkLeaderPhoneNumber)
        : null;

    const shipName = !!permitInfo.workPermitTagsMap
        ? permitInfo.workPermitTagsMap.get(WorkPermitTagType.ShipName)
        : null;

    const polygonLayer = polygonToLeafletLayer(position.polygon);

    return (
        <>
            <Marker position={[position.latitude, position.longitude]} icon={markerIcon}>
                <Popup>
                    <ul className="list-unstyled">
                        {permitUrl ? (
                            <li>
                                <a href={permitUrl} target="_blank" rel="noopener noreferrer">
                                    {versionedSequenceIdentifier(permitInfo)}
                                </a>
                            </li>
                        ) : (
                            <li>{versionedSequenceIdentifier(permitInfo)}</li>
                        )}

                        {permitInfo.startDate && permitInfo.endDate ? (
                            <li>
                                <b>{resources.get("Start")}:</b> {Date_toDateString(permitInfo.startDate)}
                                <b>{resources.get("End")}:</b> {Date_toDateString(permitInfo.endDate)}
                            </li>
                        ) : null}

                        <li>
                            <b>{resources.get("Status")}</b>: {resources.get(permitInfo.status)}
                        </li>

                        {permitInfo.entrepreneur ? (
                            <li>
                                <b>{resources.get("Entrepreneur")}</b>: {permitInfo.entrepreneur}
                            </li>
                        ) : null}

                        {permitInfo.companyName && !permitInfo.isAnonymousPermit ? (
                            <li>
                                <b>{resources.get("Company")}</b>: {permitInfo.companyName}
                            </li>
                        ) : null}

                        {contactPerson ? (
                            <li>
                                <b>{resources.get("ContactPerson")}</b>: {contactPerson.value}
                            </li>
                        ) : null}

                        {workLeaderPhoneNumber ? (
                            <li>
                                <b>{resources.get("WorkLeaderPhoneNumber")}</b>: {workLeaderPhoneNumber.value}
                            </li>
                        ) : null}

                        {shipName ? (
                            <li>
                                <b>{resources.get("ShipName")}</b>: {shipName.value}
                            </li>
                        ) : null}

                        {!permitInfo.isAnonymousPermit && permitInfo.user && permitInfo.user.phoneNumber ? (
                            <li>
                                <b>{resources.get("PermitHolderPhoneNumber")}:</b>
                                <a href={`tel:${permitInfo.user.phoneNumber}`}>{permitInfo.user.phoneNumber}</a>
                            </li>
                        ) : null}

                        {position.comment ? (
                            <li>
                                <b>{resources.get("Position")}:</b> {position.comment}
                            </li>
                        ) : null}

                        {permitInfo.description ? (
                            <li>
                                <b>{resources.get("Description")}</b>: {permitInfo.description}
                            </li>
                        ) : null}
                    </ul>
                </Popup>
            </Marker>
            {polygonLayer}
        </>
    );
}

export default PositionMarker;

function polygonToLeafletLayer(area: any): ReactNode {
    if (!area) return null;

    if (area.type === "Polygon") {
        return <Polygon positions={area.latlngs as [[number, number]]} pathOptions={area.options} />;
    }
    if (area.type === "LineString") {
        return <Polyline positions={area.latlngs as [[number, number]]} pathOptions={area.options} />;
    }

    return <Circle center={area.latlngs as [number, number]} pathOptions={area.options} radius={area.radius} />;
}
